import React from 'react';
import GroupLogo from '../GroupLogo';
import GroupLogoUpload from './GroupLogoUpload';
import GroupsTypeBadge from './GroupsTypeBadge';
import { groupTypeDotColor, groupTypeLabel } from './groupTypeStyles';

function isLastSiblingAtDepth(rows, index, depth) {
  for (let j = index + 1; j < rows.length; j += 1) {
    const nextDepth = rows[j].indentLevel || 0;
    if (nextDepth < depth) return true;
    if (nextDepth === depth) return false;
  }
  return true;
}

function getThreadMeta(rows) {
  return rows.map((row, index) => {
    const depth = row.indentLevel || 0;
    const continuations = [];
    for (let level = 0; level < depth; level += 1) {
      let continueLine = false;
      for (let j = index + 1; j < rows.length; j += 1) {
        const nextDepth = rows[j].indentLevel || 0;
        if (nextDepth <= level) break;
        continueLine = true;
        break;
      }
      continuations.push(continueLine);
    }
    const isLast = depth > 0 && isLastSiblingAtDepth(rows, index, depth);
    return { depth, continuations, isLast };
  });
}

function ThreadTreeList({ rows, threadMeta }) {
  return (
    <ul className="groups-thread-tree">
      {rows.map((row, index) => {
        const metaParts = row.metaParts || [];
        const typeLabel = row.groupType ? groupTypeLabel(row.groupType) : null;
        const { depth = 0, continuations = [], isLast = false } = threadMeta[index] || {};

        return (
          <li
            key={row.id}
            className={[
              'groups-thread-item',
              depth === 0 ? 'groups-thread-item--root' : '',
              row.groupType === 'period' ? 'groups-thread-item--period' : '',
              row.isDerivedRow ? 'groups-thread-item--derived' : '',
              row.isOwnerCrownRow ? 'groups-thread-item--owner-crown' : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {depth > 0 && !row.isOwnerCrownRow && (
              <div className="groups-thread-rail">
                {Array.from({ length: depth }).map((_, level) => {
                  const isCorner = level === depth - 1;
                  const continueLine = continuations[level];
                  let segmentClass = 'groups-thread-segment';
                  if (isCorner) {
                    segmentClass += isLast ? ' groups-thread-segment--corner-last' : ' groups-thread-segment--corner';
                  } else if (continueLine) {
                    segmentClass += ' groups-thread-segment--vcontinue';
                  }
                  return <span key={level} className={segmentClass} />;
                })}
              </div>
            )}
            <button
              type="button"
              className="groups-thread-label"
              onClick={row.onClick}
              title={row.isOwnerCrownRow ? row.metaParts?.[0] : undefined}
              aria-label={
                row.isOwnerCrownRow
                  ? `${row.metaParts?.[0] || 'Owner'}: ${row.name}`
                  : undefined
              }
            >
              {row.isOwnerCrownRow ? (
                <span className="groups-thread-owner-crown" aria-hidden>
                  👑
                </span>
              ) : null}
              <span className="groups-thread-name">{row.name}</span>
              {row.isDerivedRow ? (
                <span className="groups-thread-meta">
                  <GroupsTypeBadge groupType="derived" />
                  {metaParts.map((part, i) => (
                    <React.Fragment key={i}>
                      <span className="groups-dms-meta-sep">·</span>
                      <span>{part}</span>
                    </React.Fragment>
                  ))}
                </span>
              ) : (
                (typeLabel || metaParts.length > 0) && (
                  <span className="groups-thread-meta">
                    {typeLabel && <span>{typeLabel}</span>}
                    {typeLabel && metaParts.length > 0 && <span className="groups-dms-meta-sep">·</span>}
                    {metaParts.map((part, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="groups-dms-meta-sep">·</span>}
                        <span>{part}</span>
                      </React.Fragment>
                    ))}
                  </span>
                )
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * DMS-style bordered list, or forum-style thread tree (no row boxes).
 */
const GroupsRowList = ({
  eyebrow,
  count,
  hint,
  search = '',
  onSearch,
  searchPlaceholder = 'Search…',
  searchListSize,
  toolbar,
  loading = false,
  emptyMessage = 'No results.',
  emptyState = null,
  rows = [],
  embedded = false,
  useLogos = false,
  listVariant = 'default'
}) => {
  const listSize = searchListSize ?? rows.length;
  const showSearch = Boolean(onSearch) && listSize > 10;
  const isThread = listVariant === 'thread';
  const threadMeta = isThread ? getThreadMeta(rows) : [];

  const body = loading ? (
    <div className="groups-dms-empty">Loading…</div>
  ) : rows.length === 0 ? (
    emptyState ? (
      <div className="groups-org-list-empty-canvas">{emptyState}</div>
    ) : (
      <div className="groups-dms-empty">{emptyMessage}</div>
    )
  ) : isThread ? (
    <ThreadTreeList rows={rows} threadMeta={threadMeta} />
  ) : (
    <div className="groups-dms-list">
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1;
        const metaParts = row.metaParts || [];
        const typeLabel = row.groupType
          ? String(row.displayLabel || '').trim() || groupTypeLabel(row.groupType)
          : null;
        const depth = row.indentLevel || 0;

        const logoNode = useLogos ? (
          row.logoUploadable ? (
            <GroupLogoUpload
              groupId={row.id}
              name={row.name}
              logoUrl={row.logoUrl}
              onUploaded={row.onLogoUploaded}
              onError={row.onLogoUploadError}
              disabled={row.logoUploadDisabled}
              size="sm"
            />
          ) : (
            <GroupLogo name={row.name} logoUrl={row.logoUrl} size="sm" bare />
          )
        ) : (
          <span
            className="groups-dms-dot"
            style={{ background: groupTypeDotColor(row.groupType) }}
            aria-hidden
          />
        );

        const textNode = (
          <div className="groups-dms-row-text">
            <div className="groups-dms-row-name">{row.name}</div>
            <div className="groups-dms-row-meta">
              {row.groupType ? (
                <GroupsTypeBadge
                  groupType={row.groupType}
                  displayLabel={row.displayLabel}
                />
              ) : typeLabel ? (
                <span>{typeLabel}</span>
              ) : null}
              {(row.groupType || typeLabel) && metaParts.length > 0 && (
                <span className="groups-dms-meta-sep">·</span>
              )}
              {metaParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="groups-dms-meta-sep">·</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        );

        const rowClass = `groups-dms-row${isLast ? ' groups-dms-row--last' : ''}${
          depth ? ' groups-dms-row--indent' : ''
        }${row.logoUploadable ? ' groups-dms-row--with-logo-upload' : ''}`;
        const rowStyle = depth ? { paddingLeft: `${16 + depth * 22}px` } : undefined;

        // Logo upload is its own <button> — keep it outside the row control so
        // nested buttons don't steal/trigger open-organization.
        if (row.logoUploadable && !row.href) {
          return (
            <div key={row.id} className={rowClass} style={rowStyle}>
              <div className="groups-dms-row-left">
                {logoNode}
                <button
                  type="button"
                  className="groups-dms-row-main"
                  onClick={row.onClick}
                >
                  {textNode}
                </button>
              </div>
              {row.trailing || null}
            </div>
          );
        }

        const content = (
          <>
            <div className="groups-dms-row-left">
              {logoNode}
              {textNode}
            </div>
            {row.trailing || null}
          </>
        );

        if (row.href) {
          return (
            <a key={row.id} href={row.href} className={rowClass} style={rowStyle}>
              {content}
            </a>
          );
        }

        return (
          <button key={row.id} type="button" className={rowClass} style={rowStyle} onClick={row.onClick}>
            {content}
          </button>
        );
      })}
    </div>
  );

  if (embedded) {
    if (isThread) {
      const isEmptyWithState = !loading && rows.length === 0 && emptyState;
      const inner =
        loading || rows.length === 0 ? body : <div className="groups-thread-sheet">{body}</div>;
      return (
        <div
          className={`groups-thread-panel${isEmptyWithState ? ' groups-thread-panel--empty-center' : ''}`}
        >
          {inner}
        </div>
      );
    }
    return body;
  }

  return (
    <section className="groups-dms-section">
      {(eyebrow || toolbar || showSearch) && (
        <div className="groups-dms-section-bar">
          <div className="groups-dms-section-bar-main">
            {eyebrow && (
              <div className="groups-dms-eyebrow">
                {eyebrow}
                {typeof count === 'number' && <span className="groups-count">{count}</span>}
              </div>
            )}
            {hint && <p className="groups-dms-hint">{hint}</p>}
          </div>
          <div className="groups-dms-section-tools">
            {showSearch && (
              <input
                type="search"
                className="groups-search groups-search--compact"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
            )}
            {toolbar}
          </div>
        </div>
      )}

      {body}
    </section>
  );
};

export default GroupsRowList;
